import React, { useCallback, useMemo, useState } from 'react';
import classes from './style.module.scss';
import Modal from '~/app/common/Modal';
import { useStoreActions, useStoreState } from '~/store';
import Input from '~/app/modules/form/Search/Input';
import Icon from '~/app/common/Icon';
import useTranslation from '~/app/hooks/useTranslation';
import SelectedUserList from './SelectedUserList';
import useWebSocket from '~/app/hooks/useWebSocket';
import { getChatStoreKey, MessageType, selectChatData } from '~/app/data/statuses/chat';

export interface AddUserDialogProps {
  open: boolean
  onClose: () => void
  type: MessageType
}

export type AddUserDialogComponent = React.FC<AddUserDialogProps>;

const AddUserDialog: AddUserDialogComponent = ({ open, onClose, type }) => {

  const basePath = getChatStoreKey(type);
  const machineId = useStoreState(state =>
    type === MessageType.MACHINE ? (state.communicationChat?.data?.machineId ?? '') : ''
  );
  const projectId = useStoreState(state =>
    type === MessageType.MACHINE ? (state.communicationChat?.data?.projectId ?? '') : ''
  );
  const roleId = useStoreState(state =>
    type === MessageType.MACHINE ? (state.communicationChat?.data?.roleId ?? '') : ''
  );
  const chatSlice = useStoreState(state => state[basePath]);
  const issueId = useStoreState(state => String(selectChatData(state, type).id ?? ''));
  const isReadOnlyChat = chatSlice?.isReadOnlyChat ?? false;
  const tempChatUsers = useMemo(() => chatSlice?.tempChatUsers ?? [], [chatSlice?.tempChatUsers]);
  const fetchingSearchUsers = useStoreState(state => state.user.fetchingSearchUsers ?? []);
  const searchUserAction = useStoreActions(actions => actions.user.fetchSearchUser ?? []);
  const chatUsersFetchAction = useStoreActions(actions => actions[basePath].fetchChatUsers);
  const saveChatUserAction = useStoreActions(actions => actions[basePath].saveChatUser ?? []);
  const [searchText, setSearchText] = useState('');

  const { t } = useTranslation('user.addUser');
  const { t: t2 } = useTranslation("issue.messages");

  const { sendMessage } = useWebSocket();

  const searchUser = useCallback((searchText: string) => {
    setSearchText(searchText);
    if (searchText) {
      searchUserAction(
        type === MessageType.PROJECT
          ? { searchText, offset: 0, machineId: issueId }
          : { searchText, offset: 0 },
      );
    }
  }, [searchUserAction, type, issueId])

  const closeAll = useCallback(() => {
    setSearchText('');
    onClose();
  }, [onClose])

  const handleAddUsers = useCallback(() => {
    const machineCtx = type === MessageType.MACHINE
      ? { machineId, projectId, roleId }
      : undefined;

    saveChatUserAction({
      issueId,
      users: tempChatUsers
    }).then(res => {
      if (res.success) {
        tempChatUsers.forEach(user => {
          if (user.new) {
            sendMessage(t2("joinChatUser", { name: user.name }), 'add', issueId, type, machineCtx);
          }
          if (user.deleted) {
            sendMessage(t2("removeChatUser", { name: user.name }), 'remove', issueId, type, machineCtx);
          }
        });
        chatUsersFetchAction(issueId);
        closeAll();
      }
    })
  }, [t2, tempChatUsers, sendMessage, saveChatUserAction, issueId, chatUsersFetchAction, closeAll, type, machineId, projectId, roleId])

  return (
    <Modal className={classes['addUserDialog']} open={open} onClose={closeAll} position={'bottom'}>
      <div className={classes['userManager']}>
        <div className={classes['userHeader']}>
          <div className={classes['userSubHeader']}>
            <Icon name='arrow_back' onClick={closeAll}/>
            <span>{!isReadOnlyChat && t('add')} {t('user')}</span>
          </div>
          {!isReadOnlyChat && (
            <button
              className={classes['saveButton']}
              onClick={handleAddUsers}
            >
              {t('save')}
            </button>
          )}
        </div>
        <Input
          autoFocus={false}
          placeholder={t('search')}
          className={classes["searchInput"]}
          inputClassName={classes["input"]}
          onSearch={searchUser}
          fetching={fetchingSearchUsers}
          value={searchText}
          iconPosition='left'
        />
        <SelectedUserList searchText={searchText} type={type} />
      </div>
    </Modal>
  );
};

export default AddUserDialog;

